/**
 * Installs the session-health guard on logged-in pages. Thin wiring only — the decisions (and their
 * tests) live in src/services/sessionHealth.js, which is kept import-free. This file owns the
 * environment: Firebase auth, MSAL, the store, the router, sessionStorage and the triggers.
 * See sessionHealth.js for the full rationale (the 2026-10-08 zombie-session report) and
 * docs/plans/2026-10-08-firebase-session-health-design.md for the design.
 *
 * Triggers (each runs ONE shared check):
 *   boot        — once, on install (Layout mounted)
 *   auth-null   — Firebase signed the tab out (a revoked/expired refresh token does this)
 *   visible     — the tab comes back after being hidden > HIDDEN_RECHECK_MS (overnight tabs)
 *   denied:<x>  — a Firestore listener was denied (src/store/firestoreListen.js)
 *   http-401    — a token-gated endpoint 401'd on a current bundle (staleAuthInterceptor.js)
 *
 * Kill switch: localStorage `teamio_session_guard` = 'off' disables it for that browser.
 */

import LogRocket from 'logrocket';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { waitForAuthUser } from '@/pages/MyAccount/components/firebaseIdToken';
import microsoftAuthService from '@/services/microsoftAuth';
import { createSessionHealth, setSessionProblemHandler } from '@/services/sessionHealth';
import { DEMO_SITE_ID } from '@/utils/demo/demoSession';
import { deadSessionHold } from '@/utils/deadSessionHold';

export const KILL_SWITCH_KEY = 'teamio_session_guard';
const HIDDEN_RECHECK_MS = 15 * 60 * 1000;

function readLocal(key) {
  try { return window.localStorage.getItem(key); } catch (_e) { return null; }
}

/** The Teamio user row from login (store first, localStorage fallback). */
function currentUser(store) {
  const fromStore = store.state.customer && store.state.customer.user;
  if (fromStore) return fromStore;
  try { return JSON.parse(readLocal('user') || 'null'); } catch (_e) { return null; }
}

function siteAndEmployee(store) {
  const c = store.state.customer || {};
  const u = currentUser(store) || {};
  return {
    SiteID: c.siteID || u.SiteID || readLocal('teamioSiteID'),
    EmployeeID: c.userID || u.EmployeeID || readLocal('teamioUserID'),
  };
}

/**
 * Re-establish the Firebase identity with NO popup: only for a Microsoft user whose MSAL account
 * is still cached AND matches this Teamio user (shared computers cache other people's accounts).
 * Google sign-in always needs a click, so Google users get the prompt instead. Resolves true only
 * when the backend returned THIS employee and /select-site set this tab's site.
 */
async function silentReauth(store) {
  try {
    const user = currentUser(store) || {};
    const email = user.Username || user.email;
    const account = microsoftAuthService.findAccountByEmail(email);
    if (!account) return false;
    const accessToken = await microsoftAuthService.acquireTokenSilentOnly(account);
    if (!accessToken) return false;

    const { SiteID, EmployeeID } = siteAndEmployee(store);
    const res = await store.dispatch('customer/microsoftLogin', {
      accessToken, signInEmail: account.username, quiet: true,
    });
    const rows = res && res.data ? (Array.isArray(res.data) ? res.data : [res.data]) : [];
    if (!rows.some((r) => r && r.EmployeeID === EmployeeID)) {
      // Signed in as someone who is not this tab's employee — undo it rather than keep it.
      try { await signOut(getAuth()); } catch (_e) { /* best effort */ }
      return false;
    }
    const sel = await store.dispatch('customer/selectSite', { SiteID, EmployeeID });
    return !(sel && sel.error);
  } catch (_e) {
    return false;
  }
}

/**
 * @param {object} opts
 * @param {object} opts.store
 * @param {object} opts.router
 * @param {(state: string) => void} opts.showSignInPrompt  Layout's non-blocking "sign in again" toast
 * @returns {() => void} teardown
 */
export function installSessionHealthGuard(opts) {
  // A safety net must never take down the page it guards: Layout calls this from mounted(), so any
  // install failure (e.g. no Firebase app) is logged and the page runs without the guard.
  try {
    return install(opts);
  } catch (e) {
    console.error('[sessionHealth] guard not installed:', e);
    return () => {};
  }
}

function install({ store, router, showSignInPrompt }) {
  if (readLocal(KILL_SWITCH_KEY) === 'off') {
    console.info('[sessionHealth] disabled by localStorage', KILL_SWITCH_KEY);
    return () => {};
  }

  const auth = getAuth();
  let sawDead = false;

  const health = createSessionHealth({
    isEnabled: () => readLocal(KILL_SWITCH_KEY) !== 'off',
    isAuthenticated: () => readLocal('authenticated') === 'true',
    // Decide from the CURRENT route — never router.history._startLocation (customer/logout's
    // public-page check), which is the tab's FIRST url.
    isAuthedRoute: () => {
      const r = router.currentRoute;
      return !!(r && r.matched && r.matched.some((m) => m.meta && m.meta.requiresAuth));
    },
    getUser: () => waitForAuthUser(auth),
    getTokenResult: (user, force) => user.getIdTokenResult(force),
    expectedSiteID: () => siteAndEmployee(store).SiteID,
    // Demo site (Try Teamio): healthy without isCustomer, and never healed via /select-site or MSAL.
    demoSiteID: DEMO_SITE_ID,
    isDemoSession: () => !!(store.state.customer && store.state.customer.isDemo),
    reselectSite: async () => {
      const sel = await store.dispatch('customer/selectSite', siteAndEmployee(store));
      return { ok: !(sel && sel.error) };
    },
    silentReauth: () => silentReauth(store),
    reload: () => window.location.reload(),
    showSignInPrompt: (state) => {
      sawDead = true;
      // Dead-session hold (src/utils/deadSessionHold.js): this tab's first calls are being held for our verdict and it cannot be
      // healed, so do what the toast's click does — sign out to /login now — instead of waiting for a click while the calls pile up.
      if (deadSessionHold.isHolding()) {
        store.dispatch('customer/logout', {
          reload: true,
          msg: 'Please sign in again to continue.',
          redirectUrl: router.currentRoute && router.currentRoute.fullPath,
        });
        return;
      }
      showSignInPrompt(state);
    },
    storage: {
      get: (k) => window.sessionStorage.getItem(k),
      set: (k, v) => window.sessionStorage.setItem(k, v),
    },
    onOutcome: (outcome, trigger) => {
      try { deadSessionHold.noteOutcome(outcome); } catch (_e) { /* the hold is an extra */ }
      if (outcome === 'ok' || outcome.startsWith('skipped')) return;
      console.info('[sessionHealth]', outcome, 'trigger:', trigger);
      try { LogRocket.track('session-health', { outcome, trigger }); } catch (_e) { /* not initialised */ }
    },
  });

  setSessionProblemHandler((trigger) => health.check(trigger));

  const unsubAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      health.check('auth-null');
    } else if (sawDead && !health.isBusy()) {
      // Another tab (or the prompt's sign-in elsewhere) brought the shared Firebase identity back.
      // This tab's listeners were denied, so reload to re-subscribe them.
      window.location.reload();
    }
  });

  let hiddenAt = document.hidden ? Date.now() : 0;
  const onVisibility = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
    } else if (hiddenAt && Date.now() - hiddenAt > HIDDEN_RECHECK_MS) {
      hiddenAt = 0;
      health.check('visible');
    } else {
      hiddenAt = 0;
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  health.check('boot');

  return () => {
    setSessionProblemHandler(null);
    try { unsubAuth(); } catch (_e) { /* ignore */ }
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
